import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, Wand2, RotateCcw, Check } from "lucide-react";
import { API_BASE } from "../../lib/api";
import { OPENERS } from "../../lib/inviteDesigns";

const MODELS = [["opus", "Claude Opus 5.5", "Deepest creativity"], ["sonnet", "Claude Sonnet 5.5", "Balanced"], ["luna", "GPT 6 Luna", "Fastest"]];
const IDEAS = [
  "A Great Gatsby 1920s gala with gold fireworks over our names",
  "Soft garden romance: the veil lifts, petals fall, our love story in 4 chapters",
  "Black tie: a tuxedo opens, champagne pops, guests light three candles to enter",
  "Under the sea pearl theme with a painted ocean palace backdrop",
];
const strip = (t) => t.replace(/<design>[\s\S]*$/, "").trim();
const SPEC_KEYS = ["opener", "colors", "font", "effect", "name_fx", "interaction", "headline", "message", "scenes", "art_prompt"];

function Bubble({ m, onApply }) {
  const mine = m.role === "couple";
  return (
    <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "ml-auto bg-[#111] text-white" : "bg-[var(--bg-warm)]"}`} data-testid={`studio-msg-${m.role}`}>
      <p className="whitespace-pre-line">{m.text || (m.streaming ? "…" : "")}</p>
      {m.spec && (
        <div className="mt-2 pt-2 border-t border-black/10 flex flex-wrap items-center gap-1.5 text-[10px]">
          {m.spec.opener && <span className="rounded-full bg-white px-2 py-0.5">{OPENERS[m.spec.opener]?.name}</span>}
          {m.spec.interaction && <span className="rounded-full bg-white px-2 py-0.5">{m.spec.interaction}</span>}
          {m.spec.name_fx && <span className="rounded-full bg-white px-2 py-0.5">{m.spec.name_fx}</span>}
          {m.spec.scenes?.length > 0 && <span className="rounded-full bg-white px-2 py-0.5">{m.spec.scenes.length} scenes</span>}
          <button onClick={() => onApply(m.spec)} className="ml-auto text-[var(--gold)] font-semibold flex items-center gap-1" data-testid="studio-apply"><RotateCcw className="w-3 h-3" /> Use this version</button>
        </div>
      )}
    </div>
  );
}

export function InviteStudioChat({ inv, onSpec, onArt }) {
  const [model, setModel] = useState("sonnet");
  const [sid, setSid] = useState(() => localStorage.getItem("lif_invite_studio") || "");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);
  useEffect(() => {
    if (!sid) return;
    fetch(`${API_BASE}/weddings/mine/invite-studio/${sid}`, { credentials: "include" }).then((r) => (r.ok ? r.json() : null)).then((d) => d && setMsgs(d.messages)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function send(message) {
    const q = (message || text).trim();
    if (!q || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "couple", text: q }, { role: "designer", text: "", streaming: true }]);
    const current = Object.fromEntries(SPEC_KEYS.filter((k) => inv[k] != null).map((k) => [k, inv[k]]));
    try {
      const res = await fetch(`${API_BASE}/weddings/mine/invite-studio/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: q, model, session_id: sid, current }) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "The designer couldn't answer"); }
      const reader = res.body.getReader(), dec = new TextDecoder();
      let buf = "", full = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "session") { setSid(ev.session_id); localStorage.setItem("lif_invite_studio", ev.session_id); }
          if (ev.type === "delta") { full += ev.text; setMsgs((m) => [...m.slice(0, -1), { role: "designer", text: strip(full), streaming: true }]); }
          if (ev.type === "error") throw new Error(ev.detail);
          if (ev.type === "done") {
            setMsgs((m) => [...m.slice(0, -1), { role: "designer", text: ev.reply, spec: ev.spec }]);
            if (ev.spec) { onSpec(ev.spec); toast.success("Your invite preview has been updated"); }
          }
        }
      }
    } catch (e) { toast.error(e.message); setMsgs((m) => m.slice(0, -1)); } finally { setBusy(false); }
  }
  function reset() { localStorage.removeItem("lif_invite_studio"); setSid(""); setMsgs([]); }
  return (
    <div className="rounded-2xl overflow-hidden border border-[var(--line)] bg-white" data-testid="invite-studio">
      <div className="px-5 py-4 text-white flex flex-wrap items-center gap-3" style={{ background: "linear-gradient(120deg,#111,#2a2216)" }}>
        <Sparkles className="w-5 h-5 text-[var(--gold)]" />
        <div className="flex-1 min-w-[160px]"><p className="font-serif text-xl">AI Invite Studio</p><p className="text-[11px] text-white/60">Tell us exactly the invite you dream of. We design every scene, then keep refining as you chat.</p></div>
        <select value={model} onChange={(e) => setModel(e.target.value)} className="bg-white/10 border border-white/20 rounded-full text-xs px-3 py-1.5" data-testid="studio-model">{MODELS.map(([v, l, h]) => <option key={v} value={v} className="text-black">{l} · {h}</option>)}</select>
        {msgs.length > 0 && <button onClick={reset} className="text-[11px] underline text-white/70" data-testid="studio-reset">New chat</button>}
      </div>
      <div className="p-4 space-y-2 h-[300px] overflow-y-auto" data-testid="studio-messages">
        {!msgs.length && (
          <div className="space-y-2" data-testid="studio-ideas">
            <p className="text-xs text-[var(--muted)]">Try one of these, or describe your own:</p>
            {IDEAS.map((t, i) => <button key={t} onClick={() => send(t)} className="block w-full text-left text-sm border border-[var(--line)] rounded-xl px-3 py-2 hover:border-[var(--gold)] transition-colors" data-testid={`studio-idea-${i}`}><Wand2 className="w-3 h-3 inline mr-1.5 text-[var(--gold)]" />{t}</button>)}
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={i} m={m} onApply={onSpec} />)}
        <div ref={end} />
      </div>
      {inv.art_prompt && <div className="px-4 pb-2 flex items-center gap-2 text-xs"><Check className="w-3 h-3 text-green-600" /><span className="truncate flex-1 text-[var(--muted)]">Artwork idea: {inv.art_prompt}</span><button onClick={onArt} className="text-[var(--gold)] font-semibold shrink-0" data-testid="studio-paint">Paint it</button></div>}
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 p-3 border-t border-[var(--line)]">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} disabled={busy} placeholder={msgs.length ? "Change anything: 'make it more playful', 'add a chapter about our first date'…" : "Describe your dream animated invite…"} className="input-luxe flex-1" data-testid="studio-input" />
        <button disabled={busy || !text.trim()} className="btn-gold text-xs disabled:opacity-40" data-testid="studio-send">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {busy ? "Designing" : "Send"}</button>
      </form>
    </div>
  );
}
